import React, { Component } from "react";
import PropTypes from "prop-types";
import { Mutation } from "react-apollo";
import t from "@zendesk/client-i18n-tools";
import menuStructure from "./menuStructure";

import { KEY_CODES } from "@zendeskgarden/react-selection";

import {
  Item,
  NextItem,
  PreviousItem,
  Separator,
  MenuContainer,
  MenuView,
} from "@zendeskgarden/react-menus";

import { addFilterMutation } from "mutations/addFilterMutation";

import keyToFilter from "./keyToFilter";

import {
  SelectGroup,
  SelectView,
  Label,
  Hint,
} from "@zendeskgarden/react-select";

import styles from "./styles.css";

const setupMenu = ({ name, modal, custom, subMenus, label }, parent) => {
  const kinds = [modal, custom, subMenus && subMenus.length].filter(Boolean)
    .length;

  if (kinds > 1) {
    throw new Error(
      "A menu item can only be either have a modal, a custom menu or a sub-menu"
    );
  }

  const menu = {
    parent,
    modal,
    name,
    custom,
    label: label || `knowledge-verification.filters.${name}`,
  };

  menu.subMenus = (subMenus || []).map(subMenus => setupMenu(subMenus, menu));

  return menu;
};

const rootMenu = setupMenu(menuStructure);

export default class FilterMenu extends Component {
  static propTypes = {
    subMenus: PropTypes.node,
  };

  state = {
    focusedKey: undefined,
    isOpen: false,
    menu: rootMenu,
    searchValue: "",
  };

  getMenuContent = ({
    getItemProps,
    getNextItemProps,
    getPreviousItemProps,
    focusedKey,
  }) => {
    const { menu } = this.state;

    if (menu.custom) {
      return menu.custom({
        getItemProps,
        getNextItemProps,
        getPreviousItemProps,
        focusedKey,
      });
    }

    if (menu.subMenus.length === 0) {
      throw new Error(`No subMenus was specified for ${menu.name}`);
    }

    return (
      <React.Fragment>
        {menu.parent && (
          <PreviousItem
            {...getPreviousItemProps({
              key: menu.parent.name,
              focused: focusedKey === menu.parent.name,
            })}
            data-test-id="filter-menu-back"
          >
            {t("knowledge-verification.back")}
          </PreviousItem>
        )}
        {menu.parent && <Separator />}
        {menu.subMenus.map(
          ({ name, label, subMenus, custom }) =>
            subMenus.length > 0 || custom ? (
              <NextItem
                {...getNextItemProps({
                  key: name,
                  focused: focusedKey === name,
                })}
                data-test-id={`filter-menu-${name}`}
              >
                {t(label)}
              </NextItem>
            ) : (
              <Item
                {...getItemProps({
                  key: name,
                  focused: focusedKey === name,
                })}
                data-test-id={`filter-menu-${name}`}
              >
                {t(label)}
              </Item>
            )
        )}
      </React.Fragment>
    );
  };

  closeModal = () => {
    this.setState({ modal: null });
  };

  render() {
    const { focusedKey, isOpen, modal } = this.state;

    return (
      <React.Fragment>
        <SelectGroup className={styles.filterMenu}>
          <Label>{t("knowledge-verification.filterMenuLabel")}</Label>
          <Hint>{t("knowledge-verification.filterMenuHint")}</Hint>
          <Mutation mutation={addFilterMutation}>
            {mutate => (
              <MenuContainer
                isOpen={isOpen}
                placement="bottom-start"
                focusedKey={focusedKey}
                onStateChange={newState => {
                  if (newState.isOpen === false) {
                    // Menu is closing, we should reset the search input
                    this.setState({
                      ...newState,
                      searchValue: "",
                      menu: rootMenu,
                    });
                  } else {
                    this.setState(newState);
                  }
                }}
                onChange={selectedKey => {
                  const { menu } = this.state;

                  const subMenu = menu.subMenus.find(
                    ({ name }) => name === selectedKey
                  );

                  if (subMenu && subMenu.modal) {
                    this.setState({ modal: subMenu.modal(this.closeModal) });
                  } else if (
                    subMenu &&
                    (subMenu.subMenus.length > 0 || subMenu.custom)
                  ) {
                    this.setState({ menu: subMenu });
                  } else if (menu.parent && menu.parent.name === selectedKey) {
                    this.setState({ menu: menu.parent });
                  } else {
                    mutate({ variables: keyToFilter(selectedKey) });
                  }
                }}
                trigger={({ getTriggerProps, triggerRef, isOpen }) => (
                  <SelectView
                    {...getTriggerProps({
                      open: isOpen,
                      focused: isOpen,
                      inputRef: ref => {
                        this.triggerRef = ref;
                        triggerRef(ref);
                      },
                    })}
                    data-test-id="filter-menu-trigger"
                    style={{ width: "300px" }}
                  >
                    {t("knowledge-verification.filterMenu")}
                  </SelectView>
                )}
              >
                {({
                  getMenuProps,
                  menuRef,
                  placement,
                  getItemProps,
                  getNextItemProps,
                  getPreviousItemProps,
                  focusedKey,
                }) => (
                  <MenuView
                    {...getMenuProps({
                      placement,
                      animate: true,
                      className: styles.menu,
                      menuRef,
                      onKeyDown: event => {
                        // We don't want the space key to trigger a selection
                        // since there is a child input
                        if (event.keyCode === KEY_CODES.SPACE) {
                          event.defaultPrevented = true;
                        }
                      },
                    })}
                    data-test-id="filter-menu-popup"
                  >
                    {this.getMenuContent({
                      getItemProps,
                      getNextItemProps,
                      getPreviousItemProps,
                      focusedKey,
                      menuRef,
                    })}
                  </MenuView>
                )}
              </MenuContainer>
            )}
          </Mutation>
        </SelectGroup>
        {modal && modal}
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/FilterMenu/index.js